import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Alert, CircularProgress, Chip, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemText, Divider,
  TextField, FormControl, InputLabel, Select, MenuItem, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Rating, ToggleButton, ToggleButtonGroup, Snackbar
} from "@mui/material";
import {
  CloudUpload, Psychology, TrendingUp, Assessment, Close,
  CheckCircle, Error, AutoAwesome, School, CalendarToday,
  PersonAdd, EventSeat, Timeline, Warning, ChevronLeft, ChevronRight
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function GapAnalysis() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFile, setSelectedFile] = useState(null);
  const [employeeId, setEmployeeId] = useState("");
  const [employeeType, setEmployeeType] = useState("new_joiner");
  const [selectedProject, setSelectedProject] = useState("");
  const [projects, setProjects] = useState([]);
  const [pipEmployees, setPipEmployees] = useState([]);
  const [pipDetails, setPipDetails] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [gapAnalysisResult, setGapAnalysisResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({});

  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page')) || 1;
    setCurrentPage(pageFromUrl);
  }, [searchParams]);

  useEffect(() => {
    fetchProjects();
    fetchAnalysisHistory(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (employeeType === 'pip') {
      fetchPIPEmployees();
    } else {
      setPipEmployees([]);
      setPipDetails(null);
    }
  }, [employeeType]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(`${API_URL}/api/gap-analysis/projects`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
        
          }
        }
      );
      setProjects(response.data.projects || []);
    } catch (error) {
      //console.error("Error fetching projects:", error);
    }
  };

  const fetchAnalysisHistory = async (page = 1) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const params = new URLSearchParams({
      
        page: page.toString(),
        limit: limit.toString()
      });
      
      const response = await axios.get(`${API_URL}/api/gap-analysis/history?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
         
          }
        }
      );
      setAnalysisHistory(response.data.history || []);
      setPaginationInfo(response.data.pagination || {});
      setTotalPages(response.data.pagination?.totalPages || 1);
      
      setSearchParams({ page: page.toString() });
    } catch (error) {
      //console.error("Error fetching analysis history:", error);
    }
  };

  const fetchPIPDetails = async (empId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/improvement-plans/employee/${empId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
         
          }
        }
      );
      
      if (response.data && response.data.length > 0) {
        const activePIP = response.data.find(pip => pip.status === 'Active' || pip.status === 'In Progress');
        if (activePIP) {
          let improvementAreas = [];
          if (activePIP.improvementAreas) {
            improvementAreas = typeof activePIP.improvementAreas === 'string'
              ? JSON.parse(activePIP.improvementAreas)
              : activePIP.improvementAreas;
          }
          
          setPipDetails({
            id: activePIP.id,
            reason: activePIP.reason,
            objectives: activePIP.objectives,
            milestones: activePIP.milestones,
            startDate: activePIP.startDate,
            endDate: activePIP.endDate,
            improvementAreas
          });
          setError("");
        } else {
          setPipDetails(null);
          setError("No active PIP found for this employee");
        }
      } else {
        setPipDetails(null);
        setError("No PIP record found for this employee");
      }
    } catch (error) {
      //console.error("Error fetching PIP details:", error);
      setPipDetails(null);
      setError("Failed to fetch PIP details. Please verify employee ID.");
    }
  };

  const fetchPIPEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/improvement-plans/active-employees`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
        
          }
        }
      );
      
      if (response.data && response.data.length > 0) {
        setPipEmployees(response.data);
      } else {
        setPipEmployees([]);
        setError("No employees with active PIPs found");
      }
    } catch (error) {
      //console.error("Error fetching PIP employees:", error);
      setPipEmployees([]);
      setError("Failed to fetch PIP employees list");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        setError("Please upload a PDF, DOCX, or TXT file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError("File size should be less than 10MB");
        return;
      }
      setSelectedFile(file);
      setError("");
    }
  };

  const handleEmployeeTypeChange = (event, newType) => {
    if (newType !== null) {
      setEmployeeType(newType);
      setSelectedFile(null);
      setEmployeeId("");
      setSelectedProject("");
      setPipDetails(null);
      setPipEmployees([]);
      setError("");
      const fileInput = document.getElementById('file-upload-input');
      if (fileInput) fileInput.value = '';
    }
  };

  const handlePIPEmployeeSelect = (selectedEmpId) => {
    setEmployeeId(selectedEmpId);
    
    const selectedPipEmployee = pipEmployees.find(emp => emp.employee_id === selectedEmpId);
    if (selectedPipEmployee) {
      let improvementAreas = [];
      if (selectedPipEmployee.improvementAreas) {
        improvementAreas = typeof selectedPipEmployee.improvementAreas === 'string'
          ? JSON.parse(selectedPipEmployee.improvementAreas)
          : selectedPipEmployee.improvementAreas;
      }
      
      setPipDetails({
        id: selectedPipEmployee.pip_id,
        reason: selectedPipEmployee.reason,
        objectives: selectedPipEmployee.objectives,
        milestones: selectedPipEmployee.milestones,
        startDate: selectedPipEmployee.startDate,
        endDate: selectedPipEmployee.endDate,
        improvementAreas
      });
      setError("");
    }
  };

  const getSkillMatchPercentage = (result) => {
    if (!result) return 0;
    
    const currentSkills = result.currentSkills || [];
    const missingSkills = result.missingSkills || [];
    const requiredSkills = result.requiredSkills || [];
    
    if (requiredSkills.length > 0) {
      const matchedCount = currentSkills.length;
      const totalRequired = requiredSkills.length;
      return totalRequired > 0 ? Math.round((matchedCount / totalRequired) * 100) : 0;
    } else if (currentSkills.length > 0 || missingSkills.length > 0) {
      const totalSkills = currentSkills.length + missingSkills.length;
      return totalSkills > 0 ? Math.round((currentSkills.length / totalSkills) * 100) : 0;
    }
    
    return 0;
  };

  const handleAnalyze = async () => {
    if ((employeeType === 'new_joiner' || employeeType === 'bench') && !selectedFile) {
      setAlert({ open: true, message: "Please upload resume", severity: "warning" });
      return;
    }
    
    if (!employeeId.trim()) {
      setAlert({ open: true, message: "Please enter Employee ID", severity: "warning" });
      return;
    }
    
    if (employeeType === 'pip' && !pipDetails) {
      setAlert({ open: true, message: "No active PIP found. Please select a PIP employee.", severity: "warning" });
      return;
    }
    
    if ((employeeType === 'new_joiner' || employeeType === 'bench') && !selectedProject) {
      setAlert({ open: true, message: "Please select a project", severity: "warning" });
      return;
    }

    setUploading(true);
    setAnalyzing(true);

    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      let extractedText = "";

      if (employeeType === 'new_joiner' || employeeType === 'bench') {
        const formData = new FormData();
        formData.append('document', selectedFile);
        formData.append('employee_id', employeeId.trim());
        formData.append('project_id', selectedProject);
        formData.append('employee_type', employeeType);

        const uploadResponse = await axios.post(
          `${API_URL}/api/gap-analysis/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            }
          }
        );

        setUploading(false);

        if (uploadResponse.data.success) {
          extractedText = uploadResponse.data.extractedText;
        }
      } else {
        setUploading(false);
      }

      const analysisPayload = {
        employee_id: employeeId.trim(),
        employee_type: employeeType,
        extracted_text: extractedText
      };

      if (employeeType === 'new_joiner' || employeeType === 'bench') {
        analysisPayload.project_id = selectedProject;
      }

      if (employeeType === 'pip' && pipDetails) {
        analysisPayload.pip_objectives = pipDetails.objectives;
        analysisPayload.pip_reason = pipDetails.reason;
        analysisPayload.pip_milestones = pipDetails.milestones;
        analysisPayload.improvementAreas = pipDetails.improvementAreas;
      }

      const analysisResponse = await axios.post(
        `${API_URL}/api/gap-analysis/analyze`,
        analysisPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      setGapAnalysisResult(analysisResponse.data);
      setAlert({
        open: true,
        message: `✨ Gap analysis completed successfully for ${getEmployeeTypeLabel(employeeType)}!`,
        severity: "success"
      });
      setShowDetails(true);
      
      fetchAnalysisHistory(currentPage);
      
      setSelectedFile(null);
      setEmployeeId("");
      setSelectedProject("");
      setPipDetails(null);
      
      const fileInput = document.getElementById('file-upload-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      //console.error("Error during analysis:", error);
      const errorData = error.response?.data;

      // 1. IF PLAN DOES NOT INCLUDE AI (e.g., Basic Plan with 0 tokens)
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setAlert({
          open: true,
          message: errorData.message || "AI features are not included in your current plan. Please upgrade your plan to  unlock AI.",
          severity: "warning"
        });
      } 
      // 2. IF TOKENS ARE EXHAUSTED
      else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setAlert({
          open: true,
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle. Please recharge or upgrade your plan in BCC."}`,
          severity: "warning"
        });
      } 
      else {
        setAlert({
          open: true,
          message: errorData?.message || "Failed to analyze document. Please try again.",
          severity: "error"
        });
      }
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const getEmployeeTypeLabel = (type) => {
    switch(type) {
      case 'new_joiner': return 'New Joiner';
      case 'bench': return 'Bench Employee';
      case 'pip': return 'PIP Employee';
      default: return 'Employee';
    }
  };

  const getEmployeeTypeIcon = (type) => {
    switch(type) {
      case 'new_joiner': return <PersonAdd />;
      case 'bench': return <EventSeat />;
      case 'pip': return <Timeline />;
      default: return <PersonAdd />;
    }
  };

  const getEmployeeTypeColor = (type) => {
    switch(type) {
      case 'new_joiner': return 'primary';
      case 'bench': return 'warning';
      case 'pip': return 'error';
      default: return 'default';
    }
  };

  const viewHistoryDetails = (analysis) => {
    setGapAnalysisResult(analysis);
    setShowDetails(true);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const handleScheduleTraining = () => {
    if (!gapAnalysisResult) return;

    const trainingData = {
      employee_id: gapAnalysisResult.employee_id,
      employeeName: gapAnalysisResult.employeeName,
      employeeType: gapAnalysisResult.employeeType,
      projectName: gapAnalysisResult.projectName,
      projectId: gapAnalysisResult.projectId,
      missingSkills: gapAnalysisResult.missingSkills,
      recommendations: gapAnalysisResult.recommendations,
      fromGapAnalysis: true
    };

    localStorage.setItem('trainingData', JSON.stringify(trainingData));

    navigate('/training', { 
      state: { 
        gapAnalysisData: trainingData 
      } 
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Assessment sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            Gap Analysis
          </Typography>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle1" gutterBottom align="center" fontWeight="bold">
              Select Employee Type
            </Typography>
            <ToggleButtonGroup
              value={employeeType}
              exclusive
              onChange={handleEmployeeTypeChange}
              aria-label="employee type"
              sx={{ display: 'flex', gap: 2 }}
            >
              <ToggleButton 
                value="new_joiner" 
                aria-label="new joiner"
                sx={{ 
                  px: 3, 
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  minWidth: 150
                }}
              >
                <PersonAdd sx={{ fontSize: 32 }} />
                <Typography variant="body2" fontWeight="medium">
                  New Joiner
                </Typography>
              </ToggleButton>
              
              <ToggleButton 
                value="bench" 
                aria-label="bench employee"
                sx={{ 
                  px: 3, 
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  minWidth: 150
                }}
              >
                <EventSeat sx={{ fontSize: 32 }} />
                <Typography variant="body2" fontWeight="medium">
                  Bench Employee
                </Typography>
              </ToggleButton>
              
              <ToggleButton 
                value="pip" 
                aria-label="pip employee"
                sx={{ 
                  px: 3, 
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  minWidth: 150
                }}
              >
                <Timeline sx={{ fontSize: 32 }} />
                <Typography variant="body2" fontWeight="medium">
                  PIP Employee
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Card>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CloudUpload color="primary" />
                  <Typography variant="h6">
                    {employeeType === 'new_joiner' 
                      ? 'Upload Resume' 
                      : `${getEmployeeTypeLabel(employeeType)} Details`}
                  </Typography>
                  <Chip 
                    label={getEmployeeTypeLabel(employeeType)} 
                    color={getEmployeeTypeColor(employeeType)}
                    size="small"
                    icon={getEmployeeTypeIcon(employeeType)}
                  />
                </Box>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  {employeeType === 'new_joiner' && 'Upload resume to extract and analyze skills for new joiners'}
                  {employeeType === 'bench' && 'Upload resume to extract and analyze skills for bench employees'}
                  {employeeType === 'pip' && 'Select PIP employee - AI will analyze skills against their improvement objectives'}
                </Alert>

                {employeeType === 'pip' ? (
                  <FormControl fullWidth sx={{ mb: 2 }} required>
                    <InputLabel>Select PIP Employee</InputLabel>
                    <Select
                      value={employeeId}
                      label="Select PIP Employee"
                      onChange={(e) => handlePIPEmployeeSelect(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select an employee with active PIP</em>
                      </MenuItem>
                      {pipEmployees.map((employee) => (
                        <MenuItem key={employee.employee_id} value={employee.employee_id}>
                          {employee.employeeName} ({employee.employee_id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="Employee ID"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                  />
                )}

                {employeeType === 'pip' && pipDetails && (
                  <Card variant="outlined" sx={{ mb: 2, bgcolor: 'warning.50', border: '1px solid #ff9800' }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                        📋 Active PIP Details
                      </Typography>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          <strong style={{ color: '#d32f2f' }}>Reason:</strong>
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            pl: 2, 
                            mb: 2,
                            p: 1.5,
                            bgcolor: 'rgba(255, 152, 0, 0.08)',
                            borderRadius: 1,
                            borderLeft: '3px solid #ff9800'
                          }}
                        >
                          {pipDetails.reason || 'No reason specified'}
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          <strong style={{ color: '#1976d2' }}>Objectives:</strong>
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            pl: 2, 
                            mb: 2,
                            p: 1.5,
                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                            borderRadius: 1,
                            borderLeft: '3px solid #1976d2',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {pipDetails.objectives || 'No objectives specified'}
                        </Typography>
                        
                        {pipDetails.improvementAreas && pipDetails.improvementAreas.length > 0 && (
                          <>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              <strong style={{ color: '#9c27b0' }}>Areas Requiring Improvement:</strong>
                            </Typography>
                            <Box sx={{ 
                              pl: 2, 
                              mb: 2,
                              p: 1.5,
                              bgcolor: 'rgba(156, 39, 176, 0.08)',
                              borderRadius: 1,
                              borderLeft: '3px solid #9c27b0'
                            }}>
                              {pipDetails.improvementAreas.map((area, idx) => (
                                <Box key={idx} sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Warning sx={{ fontSize: 18, color: getPriorityColor(area.priority) + '.main', mt: 0.3 }} />
                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Chip 
                                        label={area.priority} 
                                        color={getPriorityColor(area.priority)}
                                        size="small"
                                      />
                                      <Chip 
                                        label={area.category} 
                                        variant="outlined"
                                        size="small"
                                      />
                                    </Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {area.area}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </>
                        )}
                        
                        {pipDetails.milestones && (
                          <>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              <strong style={{ color: '#2e7d32' }}>Milestones:</strong>
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                pl: 2,
                                p: 1.5,
                                bgcolor: 'rgba(46, 125, 50, 0.08)',
                                borderRadius: 1,
                                borderLeft: '3px solid #2e7d32',
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {pipDetails.milestones}
                            </Typography>
                          </>
                        )}
                        
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                <strong>Start Date:</strong>
                              </Typography>
                              <Typography variant="body2">
                                {pipDetails.startDate ? new Date(pipDetails.startDate).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                <strong>End Date:</strong>
                              </Typography>
                              <Typography variant="body2">
                                {pipDetails.endDate ? new Date(pipDetails.endDate).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {(employeeType === 'new_joiner' || employeeType === 'bench') && (
                  <FormControl fullWidth sx={{ mb: 2 }} required>
                    <InputLabel>Select Project</InputLabel>
                    <Select
                      value={selectedProject}
                      label="Select Project"
                      onChange={(e) => setSelectedProject(e.target.value)}
                    >
                      {projects.map((project) => (
                        <MenuItem key={project.project_id} value={project.project_id}>
                          {project.name} ({project.skillCount} skills)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {(employeeType === 'new_joiner' || employeeType === 'bench') && (
                  <>
                    <input
                      accept=".pdf,.doc,.docx,.txt"
                      style={{ display: 'none' }}
                      id="file-upload-input"
                      type="file"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="file-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        fullWidth
                        startIcon={<CloudUpload />}
                        sx={{ mb: 2 }}
                      >
                        Choose Resume File
                      </Button>
                    </label>

                    {selectedFile && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </Alert>
                    )}
                  </>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={analyzing ? <CircularProgress size={20} /> : <Psychology />}
                  onClick={handleAnalyze}
                  disabled={
                    (employeeType === 'new_joiner' && (!selectedFile || !selectedProject || !employeeId)) || 
                    (employeeType === 'bench' && (!selectedFile || !selectedProject || !employeeId)) ||
                    (employeeType === 'pip' && (!pipDetails || !employeeId)) ||
                    analyzing
                  }
                >
                  {uploading ? "Uploading..." : analyzing ? "Analyzing..." : "Analyze Skills"}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Info Section */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%', bgcolor: 'primary.50' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesome color="primary" />
                  How It Works - {getEmployeeTypeLabel(employeeType)}
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="1. Select Employee Type"
                      secondary={
                        employeeType === 'new_joiner' 
                          ? "For onboarding new employees - Resume upload required"
                          : employeeType === 'bench'
                          ? "For bench employees - Resume upload and project selection required"
                          : "For PIP employees - Select from active PIP list"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={employeeType === 'pip' ? '2. Select PIP Employee' : '2. Enter Employee ID'}
                      secondary={
                        employeeType === 'pip'
                          ? "Choose employee from PIP dropdown - objectives auto-load"
                          : "Provide the unique employee identifier"
                      }
                    />
                  </ListItem>
                  {(employeeType === 'new_joiner' || employeeType === 'bench') && (
                    <ListItem>
                      <ListItemText
                        primary="3. Select Project"
                        secondary="Choose target project with defined skill requirements"
                      />
                    </ListItem>
                  )}
                  {employeeType === 'pip' && (
                    <ListItem>
                      <ListItemText
                        primary="3. View PIP Details"
                        secondary="System displays PIP reason, objectives, and milestones"
                      />
                    </ListItem>
                  )}
                  {(employeeType === 'new_joiner' || employeeType === 'bench') ? (
                    <ListItem>
                      <ListItemText
                        primary="4. Upload Resume"
                        secondary="Upload resume for AI skill extraction"
                      />
                    </ListItem>
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="4. AI Analysis"
                        secondary="AI analyzes skills against PIP improvement objectives"
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemText
                      primary="5. Gap Analysis"
                      secondary={
                        employeeType === 'pip'
                          ? "AI identifies skill gaps based on PIP objectives"
                          : "AI compares skills with project requirements"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="6. Training Recommendations"
                      secondary="Get AI-powered training recommendations for skill gaps"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Analysis History */}
      {analysisHistory.length > 0 && (
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp color="primary" />
            Recent Gap Analyses
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee Type</strong></TableCell>
                  <TableCell><strong>Employee ID</strong></TableCell>
                  <TableCell><strong>Employee Name</strong></TableCell>
                  <TableCell><strong>Project</strong></TableCell>
                  <TableCell><strong>Skills Match</strong></TableCell>
                  <TableCell><strong>Missing Skills</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analysisHistory.map((analysis, index) => {
                  const matchPercentage = getSkillMatchPercentage(analysis);
                  return (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Chip
                          label={getEmployeeTypeLabel(analysis.employeeType)}
                          color={getEmployeeTypeColor(analysis.employeeType)}
                          size="small"
                          icon={getEmployeeTypeIcon(analysis.employeeType)}
                        />
                      </TableCell>
                      <TableCell>{analysis.employee_id}</TableCell>
                      <TableCell>{analysis.employeeName}</TableCell>
                      <TableCell>{analysis.projectName}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={matchPercentage}
                            sx={{ width: 100, height: 8, borderRadius: 1 }}
                          />
                          <Typography variant="body2">{matchPercentage}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={analysis.missingSkills?.length || 0}
                          color={analysis.missingSkills?.length > 5 ? "error" : "warning"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(analysis.analyzedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => viewHistoryDetails(analysis)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 1 }}>
            <Button
              variant="outlined"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              startIcon={<ChevronLeft />}
            >
              Previous
            </Button>
            
            <Typography variant="body2">
              Page {currentPage} of {totalPages} ({paginationInfo.total || 0} total)
            </Typography>
            
            <Button
              variant="outlined"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              endIcon={<ChevronRight />}
            >
              Next
            </Button>
          </Box>
        </Paper>
      )}

      {/* Results Dialog */}
      <Dialog
        open={showDetails}
        onClose={() => setShowDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment color="primary" />
              <Typography variant="h6">Gap Analysis Results</Typography>
              {gapAnalysisResult && (
                <Chip 
                  label={getEmployeeTypeLabel(gapAnalysisResult.employeeType)} 
                  color={getEmployeeTypeColor(gapAnalysisResult.employeeType)}
                  size="small"
                />
              )}
            </Box>
            <IconButton onClick={() => setShowDetails(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {gapAnalysisResult && (
            <Grid container spacing={3}>
              {/* Overview */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Overview</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Employee Type:</Typography>
                        <Chip 
                          label={getEmployeeTypeLabel(gapAnalysisResult.employeeType)} 
                          color={getEmployeeTypeColor(gapAnalysisResult.employeeType)}
                          icon={getEmployeeTypeIcon(gapAnalysisResult.employeeType)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Employee:</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {gapAnalysisResult.employeeName} ({gapAnalysisResult.employee_id})
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Project:</Typography>
                        <Typography variant="body1" fontWeight="bold">{gapAnalysisResult.projectName}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Skills Match:</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <LinearProgress
                            variant="determinate"
                            value={getSkillMatchPercentage(gapAnalysisResult)}
                            sx={{ flex: 1, height: 10, borderRadius: 1 }}
                          />
                          <Typography variant="h6" color="primary">
                            {getSkillMatchPercentage(gapAnalysisResult)}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Current Skills */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ bgcolor: 'success.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle color="success" />
                      Matching Skills ({gapAnalysisResult.currentSkills?.length || 0})
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {gapAnalysisResult.currentSkills?.map((skill, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="medium">{skill.skillName}</Typography>
                            <Chip label={skill.priority} color={getPriorityColor(skill.priority)} size="small" />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Rating value={skill.proficiency} readOnly size="small" />
                            <Typography variant="caption" color="text.secondary">
                              {skill.category}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Missing Skills */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ bgcolor: 'error.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Error color="error" />
                      Missing Skills ({gapAnalysisResult.missingSkills?.length || 0})
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {gapAnalysisResult.missingSkills?.map((skill, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="medium">{skill.skillName}</Typography>
                            <Chip label={skill.priority} color={getPriorityColor(skill.priority)} size="small" />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">Required: </Typography>
                            <Rating value={skill.requiredProficiency} readOnly size="small" />
                            <Typography variant="caption" color="text.secondary">
                              {skill.category}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Training Recommendations */}
              {gapAnalysisResult.recommendations && gapAnalysisResult.recommendations.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <School color="primary" />
                        AI-Recommended Trainings
                      </Typography>
                      <List>
                        {gapAnalysisResult.recommendations.map((rec, index) => (
                          <React.Fragment key={index}>
                            <ListItem alignItems="flex-start">
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {rec.trainingTitle}
                                  </Typography>
                                }
                                secondary={
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {rec.reason}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                      <Chip label={rec.skillCategory} size="small" color="primary" />
                                      <Chip label={`Target: ${rec.targetSkill}`} size="small" />
                                      <Chip label={`Priority: ${rec.priority}`} size="small" color={getPriorityColor(rec.priority)} />
                                    </Box>
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index < gapAnalysisResult.recommendations.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {gapAnalysisResult?.trainingRequired && (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<CalendarToday />}
              onClick={handleScheduleTraining}
            >
              Schedule Training
            </Button>
          )}
          <Button onClick={() => setShowDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Top Center Alert Popup matching Job Posting */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={() => setAlert(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={() => setAlert(prev => ({ ...prev, open: false }))} 
          severity={alert.severity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default GapAnalysis;